import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuid } from 'uuid';
import { useTranslation } from 'react-i18next';
import Table from '../../Table/Table';
import TableHead from '../../Table/TableHead';
import TableBody from '../../Table/TableBody';
import TableRow from '../../Table/TableRow';
import { formatDate } from '../../../util/helpers/dateHelpers';
import {
  transactionRequiestStatusTypesArray,
  triggerOrderStatusTypesArray,
  TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER,
} from '../../../util/enum/api/transactionTypes';
import { parseEnumType } from '../../../util/helpers/enumMappers';
import { selectIsLoadingByActionType } from '../../../store/selectors/loadingSelectors';
import { PORTFOLIO_TRANSACTIONS_SINGLE_LOADING } from '../../../store/actions/portfolio/portfolioActionConstants';
import { portfolioFinancialPendingTransactions } from '../../../store/actions/portfolio/portfolioActions';
import SectionLoader from '../../Loader/SectionLoader';
import PendingTransactionRow from './PendingTransactionRow';
import CancelTriggerOrderModal from '../../Modals/CancelTriggerOrder/CancelTriggerOrderModal';
import TriggerOrderDetailsModal from '../../Modals/TriggerOrderDetails/TriggerOrderDetailsModal';
import {
  pendingTransactionHead,
  pendingTransactionRow,
  pendingTransactionStyle,
} from '../../Table/Components/PendingTransaction';
import {
  HoldingTableEmptyStyle,
  HoldingTableEmptyTextStyle,
} from '../../Table/Components/HoldingTable';

const PendingTransactionsTable = ({
  data,
  accountUid,
  shouldShowTransactions,
  handleModalOpen,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const singleTransactionLoading = useSelector(
    selectIsLoadingByActionType(PORTFOLIO_TRANSACTIONS_SINGLE_LOADING),
  );

  const [rowData, setRowData] = useState([]);
  const [
    isCancelTriggerOrderModalOpen,
    setIsCancelTriggerOrderModalOpen,
  ] = useState(false);
  const [cancelTriggerOrderData, setCancelTriggerOrderData] = useState(null);
  const [isTriggerOrderDetailsOpen, setIsTriggerOrderDetailsOpen] = useState(
    false,
  );
  const [selectedTriggerOrder, setSelectedTriggerOrder] = useState(null);

  const refreshTransactions = () => {
    dispatch(portfolioFinancialPendingTransactions({ accountUid }));
  };

  const handleCancelTriggerOrderModalOpen = (
    triggerOrderAccountUid,
    triggerOrderUid,
  ) => {
    setCancelTriggerOrderData({
      accountUid: triggerOrderAccountUid,
      paymentUid: triggerOrderUid,
      refreshTransactions,
    });
    setIsCancelTriggerOrderModalOpen(true);
  };

  const handleCancelTriggerOrderModalClose = () => {
    setIsCancelTriggerOrderModalOpen(false);
  };

  const handleTriggerOrderDetailsOpen = (transaction) => {
    setSelectedTriggerOrder(transaction);
    setIsTriggerOrderDetailsOpen(true);
  };

  const handleTriggerOrderDetailsClose = () => {
    setIsTriggerOrderDetailsOpen(false);
  };

  const headerData = [
    {
      id: 1,
      content: t(
        'portfolio.pendingTransactions.tableHeadings.yourTransactions',
      ),
    },
    { id: 2, content: t('portfolio.pendingTransactions.tableHeadings.amount') },
    { id: 3, content: t('portfolio.pendingTransactions.tableHeadings.status') },
    { id: 4, content: '' },
  ];

  const tableBodyData = data && data?.length > 0 ? data : []; //eslint-disable-line

  useEffect(() => {
    if (tableBodyData && tableBodyData.length) {
      setRowData(
        tableBodyData.map((transaction) => {
          if (
            transaction.TransactionRequestType ===
            TRANSACTION_REQUEST_TYPE_TRIGGER_ORDER
          ) {
            const unit = t(
              transaction.Quantity > 1
                ? 'portfolio.transactions.details.pluralUnit'
                : 'portfolio.transactions.details.singleUnit',
            );

            return {
              id: transaction.TriggerOrderUid,
              content: (
                <PendingTransactionRow
                  key={`${transaction.TriggerOrderUid}-${uuid()}`}
                  date={formatDate(transaction.RequestDate, 'dd MMM yy')}
                  amount={transaction.Amount}
                  label={t('portfolio.pendingTransactions.triggerOrderLabel', {
                    quantity: transaction.Quantity,
                    unit,
                  })}
                  product={transaction.ProductCaption}
                  status={parseEnumType(
                    triggerOrderStatusTypesArray,
                    transaction.TriggerOrderStatus,
                  )}
                  canCancel={transaction.CanCancel}
                  accountUid={accountUid}
                  paymentUid={transaction.TriggerOrderUid}
                  handleModalOpen={handleCancelTriggerOrderModalOpen}
                  onRowClick={() => handleTriggerOrderDetailsOpen(transaction)}
                />
              ),
            };
          }

          return {
            id: transaction.DepositKey,
            content: (
              <PendingTransactionRow
                key={`${transaction.DepositKey}-${uuid()}`}
                date={formatDate(transaction.RequestDate, 'dd MMM yy')}
                amount={transaction.Amount}
                bankAccountNumber={transaction.BankAccountNumber}
                status={parseEnumType(
                  transactionRequiestStatusTypesArray,
                  transaction.RequestStatus,
                )}
                canCancel={transaction.CanCancel}
                action={transaction.TransactionType}
                accountUid={accountUid}
                paymentUid={transaction.DepositKey}
                handleModalOpen={handleModalOpen}
              />
            ),
          };
        }),
      );
    }
  }, [data]); //eslint-disable-line

  return (
    <>
      <Table css={pendingTransactionStyle}>
        <TableHead data={headerData} css={pendingTransactionHead} />
        <SectionLoader isLoading={singleTransactionLoading}>
          {shouldShowTransactions ? (
            <TableBody>
              <TableRow
                key={uuid()}
                css={pendingTransactionRow}
                rowData={rowData}
                data-cy="list-item-transaction-request"
              />
            </TableBody>
          ) : (
            <HoldingTableEmptyStyle isSmall>
              <HoldingTableEmptyTextStyle>
                {t('balances.noRecentTransactions')}
              </HoldingTableEmptyTextStyle>
            </HoldingTableEmptyStyle>
          )}
        </SectionLoader>
      </Table>
      <CancelTriggerOrderModal
        isOpen={isCancelTriggerOrderModalOpen}
        handleClose={handleCancelTriggerOrderModalClose}
        cancelTransactionData={cancelTriggerOrderData}
      />
      <TriggerOrderDetailsModal
        isOpen={isTriggerOrderDetailsOpen}
        onClose={handleTriggerOrderDetailsClose}
        transaction={selectedTriggerOrder}
      />
    </>
  );
};

PendingTransactionsTable.propTypes = {
  data: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        DateTime: PropTypes.string,
        Amount: PropTypes.number,
        EffectiveBalance: PropTypes.number,
        StatusType: PropTypes.number,
        Caption: PropTypes.string,
      }),
    ),
    PropTypes.shape({}),
  ]),
  accountUid: PropTypes.string,
  shouldShowTransactions: PropTypes.bool,
  handleModalOpen: PropTypes.func,
};

export default PendingTransactionsTable;
